import React, { useState } from 'react';
import theme from './OrderExportModal.module.scss';
import {
    Banner,
    getUniqueId,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Button, Radio } from '@ygtechnologies/rubus-ui';
import { OrderExportTypeKey, OrderExportTypes } from './OrderExportTypes';
import { Trans } from '@lingui/react/macro';
import { OrderSearchOptions } from '@/services/orders';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';

export interface OrderExportModalProps {
    isOpen: boolean;
    onClose(): void;
    filter: OrderSearchOptions;
    showFilterWarning?: boolean;
}

type OptionsState = {
    [key in OrderExportTypeKey]?: any;
};

function saveOptions(key: OrderExportTypeKey, options: any) {
    const storageKey = `OrderExport-${key}`;
    if (options) {
        localStorage.setItem(storageKey, JSON.stringify(options));
    } else {
        localStorage.removeItem(storageKey);
    }
}

function loadDefaultState(): OptionsState {
    const result = {};

    OrderExportTypes.forEach((type) => {
        const storageKey = `OrderExport-${type.key}`;
        try {
            const savedValue = localStorage.getItem(storageKey);
            if (savedValue) {
                result[type.key] = JSON.parse(savedValue);
            }
        } catch (e) {
            console.warn(`Invalid value in ${storageKey}`);
        }
    });

    return result;
}

export const OrderExportModal: React.FC<OrderExportModalProps> = ({
    isOpen,
    onClose,
    filter,
    showFilterWarning = false,
}) => {
    const project = useSelector((state: IState) => state.global.activeProject);
    const [selectedExportKey, setSelectedExportKey] = useState<OrderExportTypeKey>('simple');
    const [options, setOptions] = useState<OptionsState>(loadDefaultState());
    const selectedExportType = OrderExportTypes.find((x) => x.key === selectedExportKey)!;

    const handleOpen = () => {
        setSelectedExportKey(OrderExportTypes[0].key);
    };
    const handleChange = (value: any) => {
        if (!selectedExportType) {
            return;
        }

        setOptions((prev) => ({ ...prev, [selectedExportType.key]: value }));
    };

    const handleExport = () => {
        saveOptions(selectedExportKey, options[selectedExportKey]);
        onClose();
    };

    const radioName = getUniqueId();

    return (
        <Modal isOpen={isOpen} onClose={onClose} toggle={onClose} onOpen={handleOpen} className={theme.modal} size="xl">
            <ModalHeader>
                <Trans>Export</Trans>
            </ModalHeader>
            <ModalBody className={theme.body}>
                {showFilterWarning && (
                    <Banner type="warning">
                        <Trans>There is an active filter, the export will use the same filter</Trans>
                    </Banner>
                )}
                <div className={theme.main}>
                    <div className={theme.left}>
                        <ul>
                            {OrderExportTypes.filter((x) => !x.visible || x.visible(project!)).map((x) => (
                                <li key={x.key}>
                                    <Radio
                                        name={radioName}
                                        checked={x.key === selectedExportKey}
                                        onChange={(e) => setSelectedExportKey(x.key)}>
                                        {x.name()}
                                    </Radio>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={theme.right}>
                        {selectedExportType.render({ value: options[selectedExportType.key], onChange: handleChange })}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className={theme.button}
                    onClick={handleExport}
                    as="a"
                    href={selectedExportType.exportUrl(filter, options[selectedExportType.key])}
                    target="_blank">
                    <Trans>Export</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};
